import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Minus, Plus, Ticket, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';

const TicketPurchaseDialog = ({ 
    open, 
    onOpenChange, 
    event, 
    onPurchase, 
    loading = false 
}) => {
    const { language } = useLanguage();
    const [quantity, setQuantity] = useState(1);
    
    const ticketPrice = event?.ticket_price || 0;
    const totalPrice = ticketPrice * quantity;
    const isFree = ticketPrice === 0;

    const handleQuantityChange = (delta) => {
        setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));
    };

    const handlePurchase = () => {
        onPurchase(event, quantity);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {language === 'nl' ? 'Tickets Kopen' : 'Buy Tickets'}
                    </DialogTitle>
                    <DialogDescription>
                        {event?.title}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-6 space-y-6">
                    {/* Quantity Selector */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                            {language === 'nl' ? 'Aantal tickets' : 'Number of tickets'}
                        </span>
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(-1)}
                                disabled={quantity <= 1}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-2xl font-mono w-8 text-center">{quantity}</span>
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(1)}
                                disabled={quantity >= 10}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Price Summary */}
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>{quantity}x {language === 'nl' ? 'Ticket' : 'Ticket'}</span>
                            <span>€{ticketPrice.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between font-semibold text-lg pt-2 border-t border-gray-200">
                            <span>{language === 'nl' ? 'Totaal' : 'Total'}</span>
                            <span className="font-mono">
                                {isFree ? (language === 'nl' ? 'Gratis' : 'Free') : `€${totalPrice.toFixed(0)}`}
                            </span>
                        </div>
                    </div>

                    {/* Info */}
                    <p className="text-xs text-gray-500 text-center">
                        {language === 'nl' 
                            ? 'Elk ticket heeft een unieke QR-code voor toegang'
                            : 'Each ticket has a unique QR code for entry'}
                    </p>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="flex-1"
                    >
                        {language === 'nl' ? 'Annuleren' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handlePurchase}
                        disabled={loading}
                        className="flex-1 bg-[#F8BBD6] hover:bg-[#f0a8c8] text-foreground"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <Ticket className="w-4 h-4 mr-2" />
                                {isFree 
                                    ? (language === 'nl' ? 'Claim Tickets' : 'Claim Tickets')
                                    : (language === 'nl' ? 'Afrekenen' : 'Checkout')}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TicketPurchaseDialog;
